import { Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	ChangeEvent,
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
	useState,
} from 'react';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import dayjs from 'dayjs';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import {
	AGE_RANGE_LIST,
	LANGUAGE_LIST,
	LANGUAGE_ZH_LIST,
	SEX_LIST,
} from '@/common/constants/common';
import {
	useGetAreaItemListQuery,
	useGetAreaSubItemListMutation,
	useGetDicListQuery,
} from '@/api/common';
import { AreaDTO } from '@/api/DTO/common.interface';
import { Language } from '@/api/enum/common.enum';
import { eightDigitPhone } from '@/utils/validationHelper';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const EditContactInfoForm = forwardRef(
	({ type, setType, details }: Props, ref) => {
		const { t } = useTranslation();
		const [getAreaSubItemList] = useGetAreaSubItemListMutation();
		const [districtList, setDistrictList] = useState<AreaDTO[]>([]);
		const [subDistrictList, setSubDistrictList] = useState<AreaDTO[]>([]);
		const [estateCourtVillageList, setEstateCourtVillageList] = useState<
			AreaDTO[]
		>([]);
		const [buildingBlockList, setBuildingBlockList] = useState<AreaDTO[]>([]);
		const { data: areaItemList } = useGetAreaItemListQuery(
			{ type: 0 },
			{ refetchOnMountOrArgChange: true }
		);

		const defaultValues: DefaultValues<any> = {
			addressLanguage: Language.chinese,
		};

		useEffect(() => {
			if (details) {
				methods.reset(details);

				if (details.region) initAddress(details.region, 'region');
				if (details.district) initAddress(details.district, 'district');
				if (details.subdistrict)
					initAddress(details.subdistrict, 'subdistrict');
				if (details.estateCourtVillage)
					initAddress(details.estateCourtVillage, 'estateCourtVillage');
			}
		}, [details]);

		const formSchema = Yup.object().shape({
			phone: Yup.string()
				.required(t('errorMessage.pleaseEnter'))
				.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
			homeTel: Yup.string()
				.nullable()
				.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
			addressLanguage: Yup.string().required(t('errorMessage.pleaseSelect')),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
		});

		const watchAddressLanguage = methods.watch('addressLanguage');

		useImperativeHandle(ref, () => ({
			getFormData: () => methods.getValues(),
			validateForm: () => {
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		const initAddress = async (pid: string, type: string) => {
			if (!pid) return;
			const response = await getAreaSubItemList(pid || '');
			if ('data' in response) {
				if (type == 'region') setDistrictList(response?.data?.data || []);
				if (type == 'district') setSubDistrictList(response?.data?.data || []);
				if (type == 'subdistrict')
					setEstateCourtVillageList(response?.data?.data || []);
				if (type == 'estateCourtVillage')
					setBuildingBlockList(response?.data?.data || []);
			}
		};

		const handleChangeRegion = async (e: any, type: string) => {
			if (!e) return;
			const response = await getAreaSubItemList(e || '');

			if ('data' in response) {
				if (type == 'region') {
					methods.setValue('district', '');
					methods.setValue('subdistrict', '');
					methods.setValue('estateCourtVillage', '');
					methods.setValue('buildingBlock', '');
					setDistrictList(response?.data?.data || []);
				}
				if (type == 'district') {
					methods.setValue('subdistrict', '');
					methods.setValue('estateCourtVillage', '');
					methods.setValue('buildingBlock', '');
					setSubDistrictList(response?.data?.data || []);
				}
				if (type == 'subdistrict') {
					methods.setValue('estateCourtVillage', '');
					methods.setValue('buildingBlock', '');
					setEstateCourtVillageList(response?.data?.data || []);
				}
				if (type == 'estateCourtVillage') {
					methods.setValue('buildingBlock', '');
					setBuildingBlockList(response?.data?.data || []);
				}
			}
		};

		return (
			<>
				<FormProvider {...methods}>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.contactInfo')}
					</Typography>
					<form onSubmit={methods.handleSubmit((e) => e.preventDefault())}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='phone'
									label={t('preAdmission.phone') + '*'}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='homeTel'
									label={t('preAdmission.homePhone')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='email'
									label={t('preAdmission.zh_email')}
								/>
							</Grid>
							<Grid size={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.zh_addressLanguage') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='addressLanguage'
									row
									options={LANGUAGE_ZH_LIST}
								/>
							</Grid>
							{watchAddressLanguage == Language.english && (
								<>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='region'
											label={t('preAdmission.territory')}
											options={(areaItemList?.data || []).map(
												(item: { nameEn: any }) => {
													return {
														...item,
														name: item.nameEn,
													};
												}
											)}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'region');
											}}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='district'
											label={t('preAdmission.area')}
											options={(districtList || []).map((item: AreaDTO) => {
												return {
													...item,
													name: item.nameEn,
												};
											})}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'district');
											}}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='subdistrict'
											label={t('preAdmission.village')}
											options={(subDistrictList || []).map((item: AreaDTO) => {
												return {
													...item,
													name: item.nameEn,
												};
											})}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'subdistrict');
											}}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='estateCourtVillage'
											label={t('preAdmission.estate')}
											options={(estateCourtVillageList || []).map(
												(item: AreaDTO) => {
													return {
														...item,
														name: item.nameEn,
													};
												}
											)}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'estateCourtVillage');
											}}
										/>
									</Grid>
								</>
							)}
							{watchAddressLanguage == Language.chinese && (
								<>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='region'
											label={t('preAdmission.territory')}
											options={(areaItemList?.data || []).map(
												(item: { nameZh: any }) => {
													return {
														...item,
														name: item.nameZh,
													};
												}
											)}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'region');
											}}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='district'
											label={t('preAdmission.area')}
											options={(districtList || []).map((item: AreaDTO) => {
												return {
													...item,
													name: item.nameZh,
												};
											})}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'district');
											}}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='subdistrict'
											label={t('preAdmission.village')}
											options={(subDistrictList || []).map((item: AreaDTO) => {
												return {
													...item,
													name: item.nameZh,
												};
											})}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'subdistrict');
											}}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='estateCourtVillage'
											label={t('preAdmission.estate')}
											options={(estateCourtVillageList || []).map(
												(item: AreaDTO) => {
													return {
														...item,
														name: item.nameZh,
													};
												}
											)}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'estateCourtVillage');
											}}
										/>
									</Grid>
								</>
							)}

							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='roadStreet'
									label={t('preAdmission.street')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='roadStreetNo'
									label={t('preAdmission.streetNumber')}
								/>
							</Grid>
							{watchAddressLanguage == Language.english && (
								<Grid size={6}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='buildingBlock'
										label={t('preAdmission.block')}
										options={(buildingBlockList || []).map((item: AreaDTO) => {
											return {
												...item,
												name: item.nameEn,
											};
										})}
									/>
								</Grid>
							)}
							{watchAddressLanguage == Language.chinese && (
								<Grid size={6}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='buildingBlock'
										label={t('preAdmission.block')}
										options={(buildingBlockList || []).map((item: AreaDTO) => {
											return {
												...item,
												name: item.nameZh,
											};
										})}
									/>
								</Grid>
							)}

							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='buildingBlockSpecify'
									label={t('preAdmission.blockDetail')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='floor'
									label={t('preAdmission.storey')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='flat'
									label={t('preAdmission.chamber')}
								/>
							</Grid>
						</Grid>
					</form>
				</FormProvider>
			</>
		);
	}
);
EditContactInfoForm.displayName = 'EditContactInfoForm';

export default EditContactInfoForm;
